// TypeScript types mirroring contracts/ops/v1. Hand-kept in sync with
// the Go contracts package; the wire stability tests in
// contracts/ops/v1/wire_stability_test.go are the source of truth.
//
// Field naming: snake_case (matches the wire). The UI components
// destructure these directly so any drift between Go ↔ TS shows up as
// a TypeScript error rather than at runtime.

export type Severity = 1 | 2 | 3; // 1=critical, 2=warning, 3=info

// commission is SM-384's reconciler; exposure is SM-771's broker
// headroom / receivable check (contracts ops/v1 TaskTypeExposure);
// clearing is SM-782's daily broker clearing-account reconciliation
// (contracts ops/v1 TaskTypeClearing); broker is SM-724's core broker
// reconciliation (contracts ops/v1 TaskTypeBroker, phase 08d);
// provider_funding is the external-credit provider reconciliation
// (contracts ops/v1 TaskTypeProviderFunding, SM-621 phases 14–15).
export type TaskType = "balance" | "matching" | "commission" | "exposure" | "clearing" | "broker" | "provider_funding";

/**
 * Every task type, in the order the findings and runs filters offer
 * them. The one list both filters read, so a new type cannot reach one
 * page and not the other.
 */
export const TASK_TYPE_OPTIONS: { value: TaskType; label: string }[] = [
  { value: "balance", label: "Balance" },
  { value: "matching", label: "Matching" },
  { value: "commission", label: "Commission" },
  { value: "exposure", label: "Exposure" },
  { value: "clearing", label: "Clearing" },
  { value: "broker", label: "Broker" },
  { value: "provider_funding", label: "Provider funding" },
];

/** A ?task_type= value, kept only when it names a known task type. */
export function parseTaskType(v: unknown): TaskType | undefined {
  return TASK_TYPE_OPTIONS.find((o) => o.value === v)?.value;
}

export type RunStatus =
  | "running"
  | "success"
  | "failed"
  | "cancelled"
  | "partial";

// FindingCode is open-ended; Phase 2 + 3 ship 13 codes. New phases add
// more without breaking existing consumers.
export type FindingCode = string;

export interface Finding {
  schema_version: number;
  finding_id: number;
  task_type: TaskType;
  finding_code: FindingCode;
  severity: Severity;
  entity_type: string;
  entity_id: string;
  details: Record<string, unknown>;
  first_detected_at_unix_ms: number;
  last_detected_at_unix_ms: number;
  detected_count: number;
  first_run_id: number;
  last_run_id: number;
  resolved_at_unix_ms?: number;
  resolved_by?: string;
  resolution_notes?: string;
  // SM-665 case lifecycle (ops-worker 0006). Absent on an older ops-api.
  case_state?: CaseState;
  owner_admin_id?: string;
  approved_action?: string;
  case_updated_at_unix_ms?: number;
}

/** A finding's case state (contracts ops/v1 CaseState). RESOLVED ⇔ resolved. */
export type CaseState = "OPEN" | "INVESTIGATING" | "PENDING_ACTION" | "RESOLVED";

/** The states an operator sets directly; RESOLVED is reached by resolving. */
export const SETTABLE_CASE_STATES: { value: CaseState; label: string }[] = [
  { value: "OPEN", label: "Open" },
  { value: "INVESTIGATING", label: "Investigating" },
  { value: "PENDING_ACTION", label: "Pending action" },
];

export function caseStateLabel(s: CaseState | undefined): string {
  switch (s) {
    case "INVESTIGATING":
      return "Investigating";
    case "PENDING_ACTION":
      return "Pending action";
    case "RESOLVED":
      return "Resolved";
    default:
      return "Open";
  }
}

export interface FindingsPage {
  Items: Finding[];
  NextCursor: string;
}

export interface RunSummary {
  schema_version: number;
  run_id: number;
  task_def_id: number;
  task_type: TaskType;
  airflow_dag_id: string;
  airflow_run_id: string;
  trigger_kind: "scheduled" | "manual" | "backfill";
  triggered_by?: string;
  status: RunStatus;
  // scope / watermark_before / watermark_after / summary are JSONB on
  // the wire — shape varies per task type. The run-detail page renders
  // them as a watermark diff and a key-value summary.
  scope?: unknown;
  watermark_before?: Record<string, unknown>;
  watermark_after?: Record<string, unknown>;
  summary?: Record<string, unknown>;
  started_at_unix_ms: number;
  finished_at_unix_ms?: number;
  records_scanned: number;
  records_checked: number;
  findings_new: number;
  findings_recurring: number;
  error_message?: string;
  worker_count?: number;
  duration_ms?: number;
}

export interface RunsPage {
  Items: RunSummary[];
  NextCursor: string;
}

export interface RunStep {
  StepID: number;
  AirflowTaskID: string;
  TryNumber: number;
  Status: string;
  StartedAt?: string;
  FinishedAt?: string;
  DurationMS: number;
  LogURL: string;
}

export interface RunDetail {
  Run: RunSummary;
  Steps: RunStep[];
}

export interface TaskHealth {
  TaskDefID: number;
  Name: string;
  TaskType: TaskType;
  Enabled: boolean;
  LastRunStatus: RunStatus | "";
  LastRunStartedAt?: string;
  LastRunFinishedAt?: string;
  LastRunFindingsNew: number;
  OpenFindings: number;
}

export interface DashboardSummary {
  OpenCritical: number;
  OpenWarning: number;
  OpenInfo: number;
  NewLast24hCritical: number;
  NewLast24hWarning: number;
  NewLast24hInfo: number;
  Tasks: TaskHealth[];
}

export interface WalletFlowRow {
  FlowID: number;
  WalletID: string;
  Field: string;
  FlowKind: string;
  TransactionID: string;
  AmountDelta: number;
  BalanceAfter: number;
  ExpectedBalance: number;
  IsBreak: boolean;
  OccurredAt: string;
}

export interface WalletFlowsResponse {
  wallet_id: string;
  field: string;
  break_flow_id: number;
  rows: WalletFlowRow[];
}

export interface SettlementSide {
  TradeRecordID: string;
  OrderID: string;
  UserID: string;
  IsMaker: boolean;
  Side: string;
  Price: number;
  Quantity: number;
  NetSettled: number;
  FeeAmount: number;
  LedgerOpID: string;
  LedgerStatus: string;
  OccurredAt: string;
}

export interface FillSide {
  FillID: number;
  OrderID: string;
  UserID: string;
  IsMaker: boolean;
  Price: number;
  Quantity: number;
  OccurredAt: string;
}

export interface LedgerSide {
  TransactionID: string;
  WalletID: string;
  Kind: string;
  OperationID: string;
  ReferenceID: string;
  Amount: number;
  Status: string;
  CreatedAt: string;
}

export interface TradeIntegrity {
  TradeID: string;
  Settlements: SettlementSide[];
  Fills: FillSide[];
  LedgerTxns: LedgerSide[];
}

// Severity rendering helper for badges.
export function severityLabel(s: Severity): string {
  return s === 1 ? "critical" : s === 2 ? "warning" : "info";
}

export function severityColor(s: Severity): "red" | "yellow" | "gray" {
  return s === 1 ? "red" : s === 2 ? "yellow" : "gray";
}

// --- SM-725 broker integrity (phase 08d) -----------------------------

/**
 * One node × currency row of the broker-recon sweep's snapshot
 * (ops-api GET /admin/ops/aggregates/broker). Amounts are canonical 8dp
 * integer strings, one currency per row — never summed across
 * currencies (G8).
 */
export interface BrokerPosition {
  broker_id: string;
  root_broker_id: string;
  parent_broker_id?: string;
  display_name: string;
  lifecycle_state: string;
  depth: number;
  path: string[];
  currency: string;
  pool_available: string;
  pool_reserved: string;
  pool_pending: string;
  pool_total: string;
  allocated_outstanding: string;
  revenue_pending: string;
  revenue_available: string;
  payout_reserved: string;
  /** Signed (B12: positive → we owe the broker); absent without a clearing account. */
  clearing_balance?: string;
  clearing_direction?: "WE_OWE_BROKER" | "BROKER_OWES_US" | "FLAT";
  collateral: string;
  exposure_known: boolean;
  exposure: string;
  has_limit: boolean;
  limit: string;
  headroom: string;
  utilisation_bps: number;
  open_findings: number;
  open_critical: number;
  open_findings_by_code: Record<string, number>;
  run_id: number;
  computed_at_unix_ms: number;
}

export interface BrokerPositions {
  items: BrokerPosition[];
  computed_at_unix_ms: number;
  /** No broker-recon sweep has written a snapshot yet. */
  empty: boolean;
}

/** The broker task types whose findings are about broker money. */
export const BROKER_MONEY_TASK_TYPES: TaskType[] = ["broker", "exposure", "clearing"];

/** Broker lifecycle states (B10), in the order the filters offer them. */
export const BROKER_LIFECYCLE_STATES = ["DRAFT", "ACTIVE", "PAUSED", "CLOSING", "CLOSED"] as const;

// --- SM-665 provider investigation trace (SM-621 phase 15) -----------

/** What a trace query matched first (contracts ops/v1 ProviderTraceResolution). */
export type ProviderTraceResolution = "DEDUCT" | "CREDIT" | "ROLLBACK" | "INTENT" | "ORDER" | "POSITION";

/** Exactly one key is sent (ops-api GET /admin/ops/provider/trace). */
export type ProviderTraceQuery =
  | { transaction_id: string }
  | { intent_id: string }
  | { order_id: string };

export interface ProviderTraceIntent {
  intent_id: string;
  side: string;
  required_canonical: number;
  required_provider_canonical: number;
  resolution: string;
  provider_transaction_id?: string;
  reject_code?: string;
  created_at_unix_ms: number;
  resolved_at_unix_ms?: number;
}

export interface ProviderTraceOrder {
  side: string;
  status: string;
  quantity: number;
  filled_quantity: number;
  funding_state?: string;
  created_at_unix_ms: number;
}

export interface ProviderTracePrepaid {
  prepaid_canonical: number;
  consumed_canonical: number;
  credited_remainder_canonical: number;
  dust_canonical: number;
  state: string;
  release_reason?: string;
  rollback_transaction_id?: string;
  balanced: boolean;
}

export interface ProviderTraceConsumption {
  trade_id: string;
  kind: string;
  amount_canonical: number;
  created_at_unix_ms: number;
}

export interface ProviderTraceEffect {
  intent_id: string;
  kind: string;
  state: string;
  trade_id?: string;
  amount_canonical: number;
  effect_exists?: boolean;
  created_at_unix_ms: number;
}

export interface ProviderTraceCredit {
  receivable_id: string;
  kind: string;
  business_ref_kind: string;
  business_ref_id: string;
  amount_canonical: number;
  amount_provider_canonical: number;
  provider_transaction_id: string;
  state: string;
  created_at_unix_ms: number;
  updated_at_unix_ms: number;
}

/**
 * One external-credit order's money, provider transaction → funding
 * intent → order → consumption → remainder / proceeds / payout Credit →
 * finalization. Read-only: there is no amount an operator can change.
 * Amounts are canonical 8dp integers of the trace's currency.
 */
export interface ProviderTrace {
  query: string;
  resolved_by: ProviderTraceResolution;
  broker_account_id?: string;
  broker_id?: string;
  order_id?: string;
  position_id?: string;
  currency: string;
  intent?: ProviderTraceIntent;
  order?: ProviderTraceOrder;
  prepaid?: ProviderTracePrepaid;
  consumptions: ProviderTraceConsumption[];
  effects: ProviderTraceEffect[];
  credits: ProviderTraceCredit[];
  finalized: boolean;
  /** Every source that could not be read — partial, never blank. */
  gaps: string[];
}
